import "./i18n";
import { loadAppTranslations, fetchLanguagePreference, forcedLng } from "./i18n";
import i18n from "./i18n";
import { installGlobalErrorCapture, configureErrorReporting } from "./lib/errorCapture";
import { APIurl } from "./APIClient";

// Auto-recover from stale lazy-chunk 404s after a new deploy:
// Vite fires this when an import()-ed chunk's hashed filename no longer exists.
// Reload to pick up the new index.html + chunk names, with a short cooldown
// so a chunk that is genuinely broken can't trigger an infinite reload loop.
const handleStaleChunkFailure = () => {
  const KEY = "vite-preload-error-last-reload";
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  if (Date.now() - last < 10_000) return; // reloaded recently — break the loop
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
};

window.addEventListener("vite:preloadError", (event) => {
  // Vite attaches the underlying error — whose message NAMES the failing
  // resource URL — as `payload`. Always surface it: this is the difference
  // between "a chunk failed" and knowing WHICH file a network/extension/CSP
  // problem is eating.
  const payload = (event as Event & { payload?: unknown }).payload;
  console.error("[vite:preloadError]", payload);

  const KEY = "vite-preload-error-last-reload";
  const last = Number(sessionStorage.getItem(KEY) ?? 0);
  if (Date.now() - last < 10_000) {
    // Reloaded recently and it failed AGAIN — a deterministic resource
    // failure, not deploy skew. Do NOT preventDefault here: when the event
    // is defaultPrevented Vite's preload helper SKIPS the throw and the
    // dynamic import RESOLVES `undefined`, which surfaced downstream as the
    // opaque `lazy chunk "X" resolved without a default export`. Letting it
    // reject puts the real, URL-naming error into the boundary instead.
    return;
  }
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

// resilience-deep-dive.md W0 — global JS error capture. Routes uncaught
// errors + unhandled rejections (and, via ErrorBoundary, boundary-caught
// errors) into one structured, route-tagged sink so field bugs arrive as
// telemetry instead of as user reports. Skips chunk-load failures — the
// handlers above already recover those.
// Point the error sink at the API base so reportError can beacon a structured
// report to POST /clientError. Inert until that endpoint ships — the beacon is
// best-effort and swallows failures.
configureErrorReporting(APIurl);
installGlobalErrorCapture();

// React.lazy surfaces chunk-load failures as unhandled promise rejections that
// reach here before vite:preloadError can fire (e.g. when the import() itself
// resolves to `undefined.default`). Detect the shape and recover the same way.
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  const message = typeof reason === "string" ? reason : reason?.message ?? "";
  const isChunkLoadFailure =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
      message,
    );
  if (!isChunkLoadFailure) return;
  event.preventDefault();
  handleStaleChunkFailure();
});
import { Suspense, useEffect, useRef } from "react";
import { lazyWithReload } from "./utils/lazyWithReload";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "mapbox-gl/dist/mapbox-gl.css";

// Login + Dashboard are lazy-loaded like every other route below. Eager
// imports here used to pull Dashboard's full transitive tree (live drones
// list, calendar heatmap, top charts, scanner pins, stats row, intrusion
// log panel, plus everything *they* import) into the entry chunk — that
// was the dominant contributor to the 1.8 MB entry chunk size warning at
// build time. They drop into the same <Suspense> boundary as the others.
import ReactGA from "react-ga4";
import GoogleAnalytics from "./components/GoogleAnalytics.tsx";
import DynamicFavicon from "./components/DynamicFavicon";
import AppLayout from "./components/AppLayout.tsx";
import RequireCapability from "./components/RequireCapability.tsx";
import IntrusionLogSkeleton from "./components/skeletons/IntrusionLogSkeleton";
import "./css/index.css";
import { ThemeProvider } from "./components/theme-provider";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient.ts";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  useHasSubscriptions,
  useAuthenticate,
  useOrganizationScanners,
  useOrgChangeGuard,
  useNotificationEnabledMirror,
  prefetchZoneOverlays,
} from "./cacheQueries.ts";
import RemarksSseListener from "./realtime/RemarksSseListener";
import EncryptedSessionSseListener from "./realtime/EncryptedSessionSseListener";
import ScanningProvider from "./realtime/ScanningProvider";
import { ScannerStreamProvider } from "./realtime/ScannerStreamProvider";
import { MapRegistryProvider } from "./maps/MapRegistryContext.tsx";
import { performLogout } from "./auth/logout";
import {
  SESSION_SYNC_CHANNEL,
  TAB_ID,
  type SessionSyncMessage,
} from "./auth/sessionSync";

// Cross-tab session sync. A tab that switches org, logs in, or logs out
// broadcasts on this bus (see auth/sessionSync) so every other same-origin tab
// reloads and re-derives from the now-changed cookie/session: org-switched →
// new active org, login → routed into the app by the auth guard, logout →
// bounced to /login by the auth guard. The acting tab reloads/navigates itself
// separately and SKIPS its own broadcast below (senders post from a throwaway
// channel, so this long-lived receiver otherwise hears same-tab messages).
const sessionSyncChannel = new BroadcastChannel(SESSION_SYNC_CHANNEL);
sessionSyncChannel.onmessage = (e: MessageEvent<SessionSyncMessage>) => {
  // Ignore our own tab's broadcasts. Without this, a no-navigate logout (the
  // org-less login-error path calls performLogout without navigating) would
  // self-reload and wipe the on-screen error before the user can read it.
  if (e.data?.sender === TAB_ID) return;
  const type = e.data?.type;
  if (type === "org-switched" || type === "login" || type === "logout") {
    window.location.reload();
  }
};

// Every route lazy-loaded — Login + Dashboard included, so the entry
// chunk only contains the router, AppLayout, and what main.tsx needs
// at boot. Dashboard transitively imports a lot (calendar heatmap, top
// charts, scanner pins, live drones, etc.) — pulling that into the
// initial bundle was the dominant chunk-size contributor.
const Login = lazyWithReload(() => import("./pages/Login.tsx"), "Login");
const Dashboard = lazyWithReload(() => import("./pages/Dashboard.tsx"), "Dashboard");
const MapView = lazyWithReload(() => import("./pages/MapView.tsx"), "MapView");
const IntrusionLog = lazyWithReload(() => import("./pages/IntrusionLog.tsx"), "IntrusionLog");
const RoutedIntrusionLogDialog = lazyWithReload(() => import("./components/RoutedIntrusionLogDialog.tsx"), "RoutedIntrusionLogDialog");
const FlightLog = lazyWithReload(() => import("./pages/FlightLogs.tsx"), "FlightLogs");
const HeatmapPage = lazyWithReload(() => import("./pages/HeatmapPage.tsx"), "HeatmapPage");
const AlarmZones = lazyWithReload(() => import("./pages/AlarmZones.tsx"), "AlarmZones");
const Configuration = lazyWithReload(() => import("./pages/Configuration.tsx"), "Configuration");
const ConfigRemoteIDPros = lazyWithReload(() => import("./pages/ConfigRemoteIDPros.tsx"), "ConfigRemoteIDPros");
const ConfigUsers = lazyWithReload(() => import("./pages/ConfigUsers.tsx"), "ConfigUsers");
const ConfigDrones = lazyWithReload(() => import("./pages/ConfigDrones.tsx"), "ConfigDrones");
const ConfigScanners = lazyWithReload(() => import("./pages/ConfigScanners.tsx"), "ConfigScanners");
const AccountSettings = lazyWithReload(() => import("./pages/AccuntSettings.tsx"), "AccuntSettings");
const ApprovedDronesPage = lazyWithReload(() => import("./pages/ApprovedDrones.tsx"), "ApprovedDrones");
const WatchlistDronesPage = lazyWithReload(() => import("./pages/WatchlistDrones.tsx"), "WatchlistDrones");
const SmsSettings = lazyWithReload(() => import("./pages/SmsSettings.tsx"), "SmsSettings");
const SignUp = lazyWithReload(() => import("./pages/SignUp.tsx"), "SignUp");
const ForgotPassword = lazyWithReload(() => import("./pages/ForgotPassword.tsx"), "ForgotPassword");
const FinishSignUp = lazyWithReload(() => import("./pages/FinishSignUp.tsx"), "FinishSignUp");
const AddAirplate = lazyWithReload(() => import("./pages/AddAirplate.tsx"), "AddAirplate");
const AddOperatorID = lazyWithReload(() => import("./pages/AddOperatorID.tsx"), "AddOperatorID");
const Checkout = lazyWithReload(() => import("./pages/Checkout.tsx"), "Checkout");
const Success = lazyWithReload(() => import("./pages/Success.tsx"), "Success");
const Subscribe = lazyWithReload(() => import("./pages/Subscribe.tsx"), "Subscribe");
const Airmap = lazyWithReload(() => import("./pages/Airmap.tsx"), "Airmap");
const SelectOrganization = lazyWithReload(() => import("./pages/SelectOrganization.tsx"), "SelectOrganization");

ReactGA.initialize("G-BSTJDFGFH2");

const App = () => {
  const { data: user, isLoading: userLoading } = useAuthenticate();

  // After auth: load user's language preference, then load app translations
  useEffect(() => {
    if (!user) return;
    (async () => {
      // Skip backend preference when subdomain forces a language (e.g. fr.airplate.dk)
      if (!forcedLng) {
        const savedLng = await fetchLanguagePreference();
        if (savedLng && savedLng !== i18n.language) {
          await i18n.changeLanguage(savedLng);
        }
      }
      await loadAppTranslations();
    })();
  }, [user]);

  // Warm the map zone overlays in the background once authenticated, so the
  // first map visit / toggle is instant instead of waiting on the large
  // (~1.7 MB) drone-zones fetch — no matter which page the user lands on.
  // Deferred to idle so it never competes with first paint or the landed
  // route's own data. Idempotent + best-effort (see prefetchZoneOverlays).
  useEffect(() => {
    if (!user) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(() => prefetchZoneOverlays(), {
        timeout: 4000,
      });
      return () => w.cancelIdleCallback?.(id);
    }
    const id = window.setTimeout(() => prefetchZoneOverlays(), 1500);
    return () => window.clearTimeout(id);
  }, [user]);

  useOrgChangeGuard();
  // Keep the per-org notificationEnabled localStorage mirror in sync with the
  // server toggle so an OFF org never defaults back to ON until a settings visit.
  useNotificationEnabledMirror();
  const hasSubscriptionsQuery = useHasSubscriptions();
  const organizationScannersQuery = useOrganizationScanners();

  const hasSubscriptionsData = hasSubscriptionsQuery.data;
  const organizationScanners = organizationScannersQuery.data ?? [];

  // "Loading" here means the query has NEVER resolved for the current key
  // (status === "pending") — deliberately NOT a background refetch. Including
  // `fetchStatus === "fetching"` is what made the `/` route swap
  // Dashboard ⇄ MapView on every 10-minute scanner refetch and every window
  // refocus: a background refetch flipped these to true, dropped
  // hasSubscriptionOrScannerAccess to false, and remounted the whole page
  // (a brand-new Mapbox map + all panels). Gating on first-load only keeps the
  // decision steady while the data refreshes underneath.
  const subscriptionsLoading = hasSubscriptionsQuery.status === "pending";
  const scannersLoading = organizationScannersQuery.status === "pending";

  const hasSubscriptions = Array.isArray(hasSubscriptionsData)
    ? hasSubscriptionsData.length > 0
    : hasSubscriptionsData === true;
  const hasScanners = organizationScanners.length > 0;

  // Latch the Dashboard-vs-MapView decision once both queries have settled at
  // least once, so a transient pending state (e.g. an org-id flap that re-keys
  // these queries) can't briefly un-decide it and flash the other page. Same
  // pattern as AppLayout's lastOrgRef. A genuine access change is always
  // preceded by a full reload (org switch), which resets this ref — so the
  // latch never masks a real transition.
  const accessSettled = !subscriptionsLoading && !scannersLoading;
  const lastAccessRef = useRef<boolean | undefined>(undefined);
  if (accessSettled) {
    lastAccessRef.current = hasSubscriptions || hasScanners;
  }
  const hasSubscriptionOrScannerAccess = lastAccessRef.current ?? false;

  GoogleAnalytics();

  const navigate = useNavigate();

  useEffect(() => {
    // Public-paths whitelist (everything else is treated as protected).
    // Inverted from the previous protected-paths enumeration so any new
    // authenticated route automatically inherits the redirect without
    // anyone remembering to update a list — that's how /alarmzones,
    // /smsnotifications, and /select-organization silently slipped
    // through before, leaving the user stranded with a stale token.
    const PUBLIC_PATH_PREFIXES = [
      "/login",
      "/signUp",
      "/forgotlogin",
      "/airmap",
      "/addAirplate",
      "/addOperatorId",
      "/finishSignUp",
      "/checkout",
      "/success",
      "/subscribe",
      "/select-organization",
    ];
    const path = window.location.pathname;
    const isPublic = PUBLIC_PATH_PREFIXES.some(
      (p) => path === p || path.startsWith(p + "/"),
    );
    if (isPublic || userLoading || user) return;

    performLogout(navigate, "/login");
  }, [userLoading, user, navigate]);

  // Browser back-forward cache (BFCache) can resurrect this tab without
  // re-running effects or refetching queries — leaving us with a
  // long-cached "authenticated" user object even after the token TTL
  // expired. Force-invalidate the auth query on `pageshow` with
  // `persisted=true` so the next render sees the fresh `null` user and
  // the redirect effect above routes to /login. `refetchOnWindowFocus`
  // alone doesn't cover this case because focus may not re-fire during
  // a BFCache restore.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // Invalidate the auth probe so an expired token gets caught and
        // the redirect effect downstream routes to /login.
        queryClient.invalidateQueries({ queryKey: ["authenticate"] });
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  return (
    <>
      {/* shadcn ThemeProvider (class strategy: toggles "light"/"dark" on
          <html>). */}
      <ThemeProvider defaultTheme="light" storageKey="theme">
        <DynamicFavicon />
        <RemarksSseListener />
        <EncryptedSessionSseListener />
        <ScanningProvider />
        <ScannerStreamProvider />
        <Suspense>
        <Routes>
          {/* Authenticated pages with shared FeatureNavTW sidebar */}
          <Route element={<AppLayout />}>
            <Route
              path="/"
              element={
                hasSubscriptionOrScannerAccess ? <Dashboard /> : <MapView />
              }
            />
            <Route path="/intrusionlog" element={<Suspense fallback={<IntrusionLogSkeleton />}><IntrusionLog /></Suspense>}>
              <Route
                path=":macAddress/:start/:stop"
                element={<RoutedIntrusionLogDialog />}
              />
              {/* Encrypted sessions are addressed by id: their window moves
                  while they fly, so start/stop is not an identity. Two
                  segments, so it can never shadow the three-segment legacy
                  path above — including the legacy
                  `Encrypted:<imei>/<start>/<stop>` links still in the wild. */}
              <Route
                path="encrypted/:id"
                element={<RoutedIntrusionLogDialog />}
              />
            </Route>
            <Route path="/configuration" element={<Configuration />} />
            {/*
              Capability-guarded pages. These routes were previously guarded on
              authentication ALONE — the nav hid the links from non-admins, but
              typing the URL loaded the page for any signed-in member. That is
              the difference between "an operator is not shown the Users page"
              and "an operator cannot enter the Users page", and the latter is
              what the role means. The server enforces the same capabilities on
              every endpoint these pages call; this is so the UI matches.

              Each page asks for the capability it actually needs, so an
              operator reaches all of them except /configusers.
            */}
            <Route
              path="/configremoteidpros"
              element={
                <RequireCapability capability="devices:manage">
                  <ConfigRemoteIDPros />
                </RequireCapability>
              }
            />
            <Route
              path="/configusers"
              element={
                <RequireCapability capability="members:manage">
                  <ConfigUsers />
                </RequireCapability>
              }
            />
            <Route
              path="/configdrones"
              element={
                <RequireCapability capability="devices:manage">
                  <ConfigDrones />
                </RequireCapability>
              }
            />
            <Route
              path="/configscanners"
              element={
                <RequireCapability capability="devices:manage">
                  <ConfigScanners />
                </RequireCapability>
              }
            />
            <Route path="/accountsettings" element={<AccountSettings />} />
            <Route
              path="/alarmzones"
              element={
                <RequireCapability capability="zones:write">
                  <AlarmZones />
                </RequireCapability>
              }
            />
            <Route path="/flightlogs" element={<FlightLog />} />
            <Route path="/heatmap" element={<HeatmapPage />}>
              <Route
                path=":macAddress/:start/:stop"
                element={<RoutedIntrusionLogDialog />}
              />
            </Route>
            <Route
              path="/smsnotifications"
              element={
                <RequireCapability capability="alert-rules:write">
                  <SmsSettings />
                </RequireCapability>
              }
            />
            <Route
              path="/approveddrones"
              element={
                <RequireCapability capability="approved-drones:write">
                  <ApprovedDronesPage />
                </RequireCapability>
              }
            />
            <Route
              path="/watchlist"
              element={
                <RequireCapability capability="watchlist:write">
                  <WatchlistDronesPage />
                </RequireCapability>
              }
            />
            {hasSubscriptions || hasScanners ? (
              <Route path="/map" element={<MapView />} />
            ) : null}
          </Route>

          {/* Public / different nav pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/select-organization" element={<SelectOrganization />} />
          <Route path="/signUp" element={<SignUp />} />
          <Route path="/forgotlogin" element={<ForgotPassword />} />
          <Route path="/airmap" element={<Airmap />} />
          <Route path="/addAirplate" element={<AddAirplate />} />
          <Route path="/addOperatorId" element={<AddOperatorID />} />
          <Route path="finishSignUp" element={<FinishSignUp />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="success" element={<Success />} />
          <Route path="/subscribe" element={<Subscribe />} />
        </Routes>
        </Suspense>
      </ThemeProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </>
  );
};

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Failed to find the root element");

// Reuse existing root on HMR to avoid createRoot() warning
const root = (window as any).__REACT_ROOT__ ||
  ((window as any).__REACT_ROOT__ = ReactDOM.createRoot(rootElement));

root.render(
  <QueryClientProvider client={queryClient}>
    <MapRegistryProvider>
      <Router>
        <App />
      </Router>
    </MapRegistryProvider>
  </QueryClientProvider>,
);
